import {
  envRollbackChanges,
  fetchConfiguration,
  selectAppConfigById,
} from "@app/deploy";
import { useQuery, useSelector } from "@app/react";
import type { DeployAppConfigEnv } from "@app/types";
import { useEffect, useState } from "react";
import {
  Banner,
  Button,
  IconEyeClosed,
  IconEyeOpen,
  LoadingSpinner,
  Pill,
} from "../index";

/** Values read left-to-right: what the app has now → what it would have. */
type EnvChangeRow = { key: string; from: string; to: string };

type ChangeGroup = {
  label: string;
  variant: "progress" | "error" | "success";
  rows: EnvChangeRow[];
};

function EnvValue({
  value,
  emphasized,
}: {
  value: string;
  emphasized: boolean;
}) {
  if (value === "") {
    return <span className="text-black-300">{'""'}</span>;
  }

  return (
    <span
      className={`break-all ${emphasized ? "text-black" : "text-black-500"}`}
    >
      {value}
    </span>
  );
}

function EnvChangeGroup({ group }: { group: ChangeGroup }) {
  return (
    <div className="flex items-start gap-4 py-3 first:pt-0 last:pb-0">
      <div className="w-[76px] shrink-0">
        <Pill variant={group.variant}>{group.label}</Pill>
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        {group.rows.map(({ key, from, to }) => (
          <div
            key={key}
            className="font-mono text-xs flex items-baseline gap-2 flex-wrap"
          >
            <span className="font-semibold text-black">{key}:</span>
            <EnvValue value={from} emphasized={false} />
            <span className="text-black-300" aria-label="becomes">
              →
            </span>
            <EnvValue value={to} emphasized={true} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function envChangeGroups(
  historical: DeployAppConfigEnv,
  current: DeployAppConfigEnv,
): ChangeGroup[] {
  const { removed, changed, restored } = envRollbackChanges(
    historical,
    current,
  );

  const row = (key: string): EnvChangeRow => ({
    key,
    from: String(current[key] ?? ""),
    to: String(historical[key] ?? ""),
  });

  return [
    { label: "Change", variant: "progress", rows: changed.map(row) },
    { label: "Add", variant: "success", rows: restored.map(row) },
    { label: "Remove", variant: "error", rows: removed.map(row) },
  ].filter((group) => group.rows.length > 0) as ChangeGroup[];
}

export function RollbackEnvChanges({
  targetConfigurationId,
  currentConfigurationId,
  onEmptyChange,
}: {
  targetConfigurationId: string;
  currentConfigurationId: string;
  onEmptyChange?: (isEmpty: boolean) => void;
}) {
  // Values start hidden: these configurations are fetched with sensitive
  // extras, so the diff can contain credentials.
  const [showValues, setShowValues] = useState(false);

  const targetAction = fetchConfiguration({
    id: targetConfigurationId,
    withSensitiveExtras: true,
  });
  const currentAction = fetchConfiguration({
    id: currentConfigurationId,
    withSensitiveExtras: true,
  });
  const targetLoader = useQuery(targetAction);
  const currentLoader = useQuery(currentAction);

  const targetConfig = useSelector((s) =>
    selectAppConfigById(s, { id: targetConfigurationId }),
  );
  const currentConfig = useSelector((s) =>
    selectAppConfigById(s, { id: currentConfigurationId }),
  );

  const isLoading = targetLoader.isLoading || currentLoader.isLoading;
  const isError = targetLoader.isError || currentLoader.isError;
  const groups = envChangeGroups(targetConfig.env, currentConfig.env);
  const isEmpty = !isLoading && !isError && groups.length === 0;

  useEffect(() => {
    onEmptyChange?.(isEmpty);
  }, [isEmpty, onEmptyChange]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-black-500">
        <LoadingSpinner /> Checking which environment variables would change…
      </div>
    );
  }

  if (isError) {
    return (
      <Banner variant="warning">
        Couldn't load the environment variables for these deployments, so the
        list of changes can't be shown. Restoring will still replace the app's
        current variables with the ones from the selected deployment.
      </Banner>
    );
  }

  if (isEmpty) {
    return (
      <p className="text-sm text-black-500">
        No environment variables would change.
      </p>
    );
  }

  const count = groups.reduce((total, group) => total + group.rows.length, 0);

  return (
    <div className="border border-black-100 rounded-lg bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-black">
          {count} Environment Variable{count === 1 ? "" : "s"} will be impacted
        </p>
        <Button
          variant="white"
          size="xs"
          onClick={() => setShowValues(!showValues)}
        >
          {showValues ? (
            <IconEyeClosed variant="sm" className="mr-1" />
          ) : (
            <IconEyeOpen variant="sm" className="mr-1" />
          )}
          {showValues ? "Hide values" : "Show values"}
        </Button>
      </div>
      {showValues ? (
        <div className="flex flex-col divide-y divide-black-100 border-t border-black-100 mt-4 pt-4">
          {groups.map((group) => (
            <EnvChangeGroup key={group.label} group={group} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
